<!-- Dashboard home content - stats, chart, main functions, news -->
<div class="dashboard-home">
  <div class="retention-cards-row">
    <div
      class="panel retention-card train-next-card"
      [class.training-target-card]="trainNext?.recommendationType === 'SET_COURSE_GOAL'">
      @if (trainNextLoading) {
        <p class="retention-muted">Loading your next training step...</p>
      } @else if (trainNext) {
        @if (trainNext.recommendationType === 'SET_COURSE_GOAL' && trainNext.goalSetup) {
          <div class="retention-card-header">
            <div>
              <span class="retention-kicker retention-kicker-accent">Set your plan</span>
              <h3 class="section-title">Set your training target</h3>
            </div>
            <span class="training-target-badge">
              <i class="bi bi-stars" aria-hidden="true"></i>
              Recommended
            </span>
          </div>
          <p class="training-target-intro">
            Choose a pace to get a daily question goal and a clear finish-date forecast. You can change it anytime.
          </p>
          <div class="goal-setup">
            <div class="goal-course-row">
              @if (planningCourses.length > 1) {
                <label class="goal-course-picker">
                  <span class="retention-meta-label">Course</span>
                  <select
                    [ngModel]="currentCourseId"
                    (ngModelChange)="onDashboardCourseChange($event)"
                    aria-label="Training course"
                  >
                    @for (course of planningCourses; track course.id) {
                      <option [ngValue]="course.id">{{ course.name }}</option>
                    }
                  </select>
                </label>
              } @else if (currentCourseName) {
                <div>
                  <span class="retention-meta-label">Course</span>
                  <strong>{{ currentCourseName }}</strong>
                </div>
              }
            </div>
            <div class="goal-summary">
              <div>
                <span class="retention-meta-label">Course questions</span>
                <strong>{{ trainNext.goalSetup.totalQuestions }}</strong>
              </div>
              <div>
                <span class="retention-meta-label">Already seen</span>
                <strong>{{ trainNext.goalSetup.questionsSeen }}</strong>
              </div>
              <div>
                <span class="retention-meta-label">Still open</span>
                <strong>{{ trainNext.goalSetup.remainingQuestions }}</strong>
              </div>
            </div>

            <div class="goal-presets" aria-label="Learning target presets">
              @for (preset of trainNext.goalSetup.presets; track preset.id) {
                <button
                  type="button"
                  class="goal-preset"
                  [class.goal-preset-selected]="selectedGoalPresetId === preset.id && !customGoalDate"
                  (click)="selectGoalPreset(preset.id)"
                >
                  <span>{{ preset.label }}</span>
                  <strong>{{ preset.dailyQuestions }}/day</strong>
                  <small>{{ preset.months }} months</small>
                </button>
              }
            </div>

            <label class="custom-goal-date">
              <span>Custom target date</span>
              <input type="date" [(ngModel)]="customGoalDate" (ngModelChange)="onCustomGoalDateChange()" />
            </label>

            <button
              type="button"
              class="retention-primary-action"
              [disabled]="goalSaving"
              (click)="saveSelectedGoal()"
            >
              {{ goalSaving ? 'Saving...' : 'Save target' }}
            </button>
          </div>
        } @else {
          <div class="train-next-layout">
              <div class="train-next-header">
                <div>
                  <span class="retention-kicker retention-kicker-accent">Today</span>
                  <h3 class="section-title train-next-command">{{ trainNext.ctaLabel }}</h3>
                </div>
                @if (trainNext.dailyTarget.streakDay) {
                  <span class="retention-pill">{{ trainNext.dailyTarget.streakDay }} day streak</span>
                }
              </div>
              <p class="train-next-reason">{{ trainNext.reason }}</p>
              @if (nextMilestone) {
                <div class="coverage-bar-block" data-testid="train-next-coverage">
                  <div class="coverage-bar-header">
                    <span class="coverage-bar-title">
                      @if (isCoverageMilestone(nextMilestone)) {
                        <span
                          class="coverage-help"
                          [class.coverage-help-open]="coverageHelpOpen"
                          data-testid="milestone-coverage-help"
                          (click)="$event.stopPropagation()"
                        >
                          <button
                            type="button"
                            class="coverage-help-trigger"
                            (click)="onCoverageHelpClick($event)"
                            [attr.aria-expanded]="coverageHelpOpen"
                            aria-label="About milestone coverage"
                          >
                            <span class="coverage-bar-highlight">{{ formatMilestonePercent(nextMilestone.threshold) }}</span>
                            milestone coverage
                            <i class="bi bi-info-circle" aria-hidden="true"></i>
                          </button>
                          <span class="coverage-help-popover" role="tooltip">
                            {{ milestoneCoverageTooltip(nextMilestone) }}
                          </span>
                        </span>
                      } @else if (milestoneTitleLead(nextMilestone)) {
                        <span class="coverage-bar-highlight">{{ milestoneTitleLead(nextMilestone) }}</span>
                        {{ milestoneTitleRest(nextMilestone) }}
                      } @else {
                        {{ nextMilestone.title }}
                      }
                    </span>
                    <span class="coverage-bar-meta">
                      {{ nextMilestone.subjectName ? abbreviateSubject(nextMilestone.subjectName) : 'Course progress' }}
                      · {{ nextMilestoneProgressLabel(nextMilestone) }}
                    </span>
                  </div>
                  <div
                    class="coverage-bar-track"
                    role="progressbar"
                    [attr.aria-valuenow]="nextMilestoneProgressPercent(nextMilestone)"
                    aria-valuemin="0"
                    aria-valuemax="100"
                    [attr.aria-label]="nextMilestone.title"
                  >
                    <div class="coverage-bar-fill" [style.width.%]="nextMilestoneProgressPercent(nextMilestone)"></div>
                  </div>
                </div>
              }
              <div class="retention-meta-stack">
                <div class="retention-meta-chip">
                  <span class="retention-meta-icon" aria-hidden="true"><i class="bi bi-calendar3"></i></span>
                  <div class="retention-meta-copy">
                    <span class="retention-meta-label">Today</span>
                    <strong>{{ trainNext.dailyTarget.answeredToday }} / {{ trainNext.dailyTarget.plannedQuestions }}</strong>
                  </div>
                </div>
                <div class="retention-meta-chip">
                  <span class="retention-meta-icon" aria-hidden="true"><i class="bi bi-file-earmark-text"></i></span>
                  <div class="retention-meta-copy">
                    <span class="retention-meta-label">Due reviews</span>
                    <strong>{{ trainNext.readiness.dueReviews }}</strong>
                  </div>
                </div>
                <div class="retention-meta-chip retention-meta-chip-subject">
                  <span class="retention-meta-icon" aria-hidden="true"><i class="bi bi-book"></i></span>
                  <div class="retention-meta-copy">
                    <span class="retention-meta-label">Subject</span>
                    <strong [title]="trainNext.subjectName || 'Course setup'">
                      {{ abbreviateSubject(trainNext.subjectName) }}
                    </strong>
                  </div>
                </div>
              </div>
              <div class="train-next-actions">
                @if (canStartNewTest) {
                  <button type="button" class="retention-primary-action train-next-action-btn" (click)="openTrainNextTest()">
                    <i class="bi bi-play-fill" aria-hidden="true"></i>
                    Open New Test
                  </button>
                } @else {
                  <button type="button" class="retention-primary-action train-next-action-btn retention-primary-action-disabled" disabled>
                    <i class="bi bi-play-fill" aria-hidden="true"></i>
                    Open New Test
                    @if (hasExpiredTrial) {
                      <span class="access-badge">Trial expired</span>
                    }
                  </button>
                }
              </div>
          </div>
        }
      } @else {
        <p class="retention-muted">Set a course target to get daily training guidance.</p>
      }
    </div>

    <div class="panel retention-card coach-card" *ngIf="brand.showMascot">
      <app-sky-coach-panel [context]="skyCoachContext()" />
    </div>
  </div>

  <div class="stats-chart-row">
    <div class="stats-stack">
      <div class="panel stat-card">
        <div class="stat-card-content">
          <span class="stat-label">Average Score</span>
          <span class="stat-value">{{ averageScore.toFixed(2) }}%</span>
        </div>
        <div class="stat-card-icon stat-icon-chart">
          <i class="bi bi-graph-up"></i>
        </div>
      </div>
      <div class="panel stat-card">
        <div class="stat-card-content">
          <span class="stat-label">Questions Seen</span>
          <span class="stat-value">{{ questionsSeen }}</span>
        </div>
        <div class="stat-card-icon stat-icon-target">
          <i class="bi bi-bullseye"></i>
        </div>
      </div>
      <div class="panel stat-card">
        <div class="stat-card-content">
          <span class="stat-label">Tests Done</span>
          <span class="stat-value">{{ testsDone }}</span>
        </div>
        <div class="stat-card-icon stat-icon-check">
          <i class="bi bi-clipboard-check"></i>
        </div>
      </div>
      <div class="panel stat-card stat-card-xp">
        <div class="stat-card-content">
          <span class="stat-label">XP</span>
          <span class="stat-value">{{ xpTotal }}</span>
          <span class="stat-subvalue">Level {{ xpLevel }} | +{{ xpToday }} today | {{ xpWeek }} this week</span>
          @if (retentionFlags.leaderboardEnabled()) {
            <button type="button" class="stat-leaderboard-btn" data-testid="leaderboard-rank-button" (click)="openLeaderboardModal()">
              {{ leaderboardRankLabel() }}
            </button>
          }
        </div>
        <div class="stat-card-icon stat-icon-xp">
          <i class="bi bi-stars"></i>
        </div>
      </div>
    </div>
    <div class="panel chart-card" [class.chart-card-with-plane]="brand.showMascot">
    <i *ngIf="brand.showMascot" class="bi bi-airplane chart-plane-silhouette" aria-hidden="true"></i>
    <h3 class="section-title">Performance Over Time</h3>
    <div id="performanceChart">
      @if (chartHasData) {
        <apx-chart
          [series]="chartOptions.series!"
          [chart]="chartOptions.chart!"
          [dataLabels]="chartOptions.dataLabels!"
          [plotOptions]="chartOptions.plotOptions!"
          [xaxis]="chartOptions.xaxis!"
          [yaxis]="chartOptions.yaxis!"
          [legend]="chartOptions.legend!"
          [fill]="chartOptions.fill!"
          [stroke]="chartOptions.stroke!"
          [tooltip]="chartOptions.tooltip!"
          [grid]="chartOptions.grid!"
          [markers]="chartOptions.markers!"
        ></apx-chart>
      } @else {
        <div class="chart-empty">
          <i class="bi bi-graph-up-arrow chart-empty-illu" aria-hidden="true"></i>
          <p class="chart-empty-text">Take a test to see your scores trend over time.</p>
        </div>
      }
    </div>
    </div>
  </div>

  @if (!chartHasData) {
    <div class="welcome-card" aria-label="Get started">
      <div class="welcome-row">
        <div class="welcome-icon"><i class="bi bi-mortarboard-fill" aria-hidden="true"></i></div>
        <div class="welcome-text">
          <h4>Welcome to {{ brand.productName }}</h4>
          <p>Practice questions, save the tricky ones, and track your progress.</p>
        </div>
      </div>
      <div class="welcome-actions">
        @if (canStartNewTest) {
          <a routerLink="/question-pool" class="welcome-btn welcome-btn-primary">
            <i class="bi bi-plus-circle-fill" aria-hidden="true"></i>
            New test
          </a>
        } @else {
          <button type="button" class="welcome-btn welcome-btn-primary welcome-btn-disabled" disabled [title]="newTestBlockedReason">
            <i class="bi bi-plus-circle-fill" aria-hidden="true"></i>
            New test
            @if (hasExpiredTrial) {
              <span class="access-badge">Trial expired</span>
            }
          </button>
        }
        <a routerLink="/search-question" class="welcome-btn welcome-btn-secondary">
          <i class="bi bi-search" aria-hidden="true"></i>
          Browse questions
        </a>
      </div>
    </div>
  }

  <div class="panel main-functions-panel">
    <h3 class="section-title">Main Functions</h3>
    <div class="main-function-cards">
      @if (canStartNewTest) {
        <a routerLink="/question-pool" class="main-function-card main-function-new" data-testid="dashboard-new-test-card">
          <div class="main-function-icon"><i class="bi bi-file-earmark-plus"></i></div>
          <span class="card-title">New Test</span>
          <span class="card-subtitle">Start a new practice test</span>
        </a>
      } @else {
        <div
          class="main-function-card main-function-new main-function-disabled"
          data-testid="dashboard-new-test-card"
          role="button"
          aria-disabled="true"
          [attr.title]="newTestBlockedReason">
          <div class="main-function-icon"><i class="bi bi-file-earmark-plus"></i></div>
          <span class="card-title">New Test</span>
          <span class="card-subtitle">{{ newTestBlockedReason }}</span>
          @if (hasExpiredTrial) {
            <span class="access-badge access-badge-card">Trial expired</span>
          }
        </div>
      }
      <a routerLink="/saved-tests" class="main-function-card main-function-saved">
        <div class="main-function-icon"><i class="bi bi-bookmark-check"></i></div>
        <span class="card-title">Saved Tests</span>
        <span class="card-subtitle">Review your saved tests</span>
      </a>
      @if (retentionFlags.studyTogetherEnabled()) {
        <a routerLink="/study-together" class="main-function-card main-function-shared" data-testid="dashboard-study-together-card">
          <div class="main-function-icon"><i class="bi bi-people"></i></div>
          <span class="card-title">Study together</span>
          <span class="card-subtitle">Shared tests and friends</span>
          @if (socialBadgeCount > 0) {
            <span class="access-badge access-badge-card">{{ socialBadgeCount }}</span>
          }
        </a>
      }
      <a routerLink="/search-question" class="main-function-card main-function-search">
        <div class="main-function-icon"><i class="bi bi-search"></i></div>
        <span class="card-title">Search Question</span>
        <span class="card-subtitle">Find specific questions</span>
      </a>
    </div>
  </div>

  <div class="panel news-card">
    <div class="news-section-header">
      <h3 class="section-title"><i class="bi bi-bell"></i> News</h3>
      <i class="bi bi-chevron-right news-section-arrow"></i>
    </div>
    @if (newsLoading) {
      <p class="news-empty">Loading news…</p>
    } @else if (newsItems.length === 0) {
      <p class="news-empty">No news at the moment.</p>
    } @else {
      <div class="news-list">
        @for (item of newsItems; track item.id) {
          <div class="news-item">
            <span class="news-badge">Announcement</span>
            <div class="news-item-header">
              <h4 class="news-headline">{{ item.headline }}</h4>
              <span class="news-meta">{{ item.createdAt | date:'yyyy-MM-dd' }} | {{ item.createdAt | date:'HH:mm:ss' }}</span>
            </div>
            <div class="news-text" [innerHTML]="item.text | safeHtml"></div>
          </div>
        }
      </div>
    }
  </div>

  @if (leaderboardOpen) {
    <div class="leaderboard-backdrop" (click)="closeLeaderboardModal()" aria-hidden="true"></div>
    <div class="leaderboard-modal" role="dialog" aria-labelledby="leaderboardModalTitle" data-testid="leaderboard-modal">
      <div class="leaderboard-modal-header">
        <div>
          <span class="retention-kicker">This week</span>
          <h3 id="leaderboardModalTitle">Weekly leaderboard</h3>
          @if (leaderboard?.startsAt && leaderboard?.endsAt) {
            <p class="leaderboard-period">{{ leaderboard!.startsAt }} – {{ leaderboard!.endsAt }}</p>
          }
        </div>
        <button type="button" class="btn-close" (click)="closeLeaderboardModal()" aria-label="Close"></button>
      </div>
      @if (leaderboardLoading) {
        <p class="retention-muted">Loading weekly standings…</p>
      } @else if (leaderboard) {
        @if (leaderboard.climb?.headline) {
          <p class="leaderboard-climb-headline" data-testid="leaderboard-climb-headline">{{ leaderboard.climb!.headline }}</p>
        }
        @if (!leaderboard.optedIn) {
          <p class="retention-muted">Join the weekly leaderboard in Settings to appear in the standings.</p>
        } @else if (leaderboard.viewerRank) {
          <p class="leaderboard-viewer" data-testid="leaderboard-viewer-row">
            Your rank: <strong>#{{ leaderboard.viewerRank }}</strong> · {{ leaderboard.viewerXp ?? 0 }} this week
          </p>
        }
        <ol class="leaderboard-list">
          @for (entry of leaderboard.entries; track entry.rank) {
            <li [attr.data-testid]="'leaderboard-entry-' + entry.rank" [class.leaderboard-row-viewer]="entry.viewer">
              <span>#{{ entry.rank }}</span>
              <span>{{ entry.displayName }}</span>
              <strong class="leaderboard-entry-xp">
                <span>{{ entry.xp }}</span>
                <small>this week</small>
              </strong>
            </li>
          }
        </ol>
        <p class="retention-muted leaderboard-footnote">Resets Monday · Ranked by XP earned this week</p>
      }
    </div>
  }
</div>
